import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { DropdownOptionType } from '@Components/Dropdown/Type';

import {
  onForward,
  IThinkLogisticsDomesticInformationValidation,
  getWarehouses,
  addWarehouse,
  OrderTypeOptions,
  ServiceTypeOptions,
  PaymentModeOptions,
  LogisticsOptions,
} from './constants';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const IThinkLogisticsDomesticInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showFedExDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, iThinkLogisticsDomesticDetails } = useSelector(
    (state: RootStateType) => state.adminShipment,
  );

  const iThinkLogisticsDomesticSettings = useSelector(
    (state: RootStateType) => state.user.carrierSetting.ithink_logistics_domestic,
  );
  const [pickupAddressOptions, setPickupAddressOptions] = useState<DropdownOptionType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Hardcoded "Add Warehouse" option
  const addWarehouseOption: DropdownOptionType = {
    value: 'add_warehouse',
    display: '+ Add Warehouse',
  };

  // Combine add warehouse option with fetched warehouses
  const combinedPickupAddressOptions = [addWarehouseOption, ...pickupAddressOptions];

  const fetchWarehouses = async (accountNumber: string) => {
    try {
      const warehouses = await getWarehouses(accountNumber);
      setPickupAddressOptions(warehouses);
    } catch (error) {
      console.error('Failed to fetch warehouses', error);
    }
  };

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  useEffect(() => {
    const accountNumber = accountOptions?.[0]?.value.split('-')[0];
    if (accountNumber && shipmentById?.buyingPricing?.code === 'ithink_logistics_domestic') {
      fetchWarehouses(accountNumber);
    }
  }, [shipmentById, accountOptions]);

  const handleWarehouseChange = async (value: string[]) => {
    const selectedValue = value[0] || '';

    if (selectedValue === 'add_warehouse') {
      // Handle "Add Warehouse" option click
      if (!values.accountNo?.length) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: 'Please select an iThink Logistics Account first',
          }),
        );
        setFieldValue('pickup_address_id', '');
        return;
      }

      setIsLoading(true);
      try {
        // Extract account number from the selected account
        const accountNumber = values.accountNo.split('-')[0];
        const shipmentNumber = shipmentById?.shipmentNumber || '';
        await addWarehouse({ accountNo: accountNumber, shipmentNumber });

        // Refresh the warehouses list after successful addition
        await fetchWarehouses(accountNumber);

        // Optionally select the newly added warehouse automatically
        // You might want to adjust this based on your API response
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success!',
            subTitle: 'Warehouse added successfully!',
          }),
        );
      } catch (error) {
        dispatch(
          SuccessErrorModalOpen({
            type: 'error',
            title: 'Oops!',
            subTitle: error?.response?.data?.msg || 'Failed to add warehouse. Please try again.',
          }),
        );
        console.error('Error adding warehouse:', error);
      } finally {
        setIsLoading(false);
        setFieldValue('pickup_address_id', '');
      }
    } else {
      // Normal warehouse selection
      setFieldValue('pickup_address_id', selectedValue);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      logistics: '',
      order_type: '',
      pickup_address_id: '',
      s_type: '',
      payment_mode: '',
      ...initialStates,
    },
    validationSchema: IThinkLogisticsDomesticInformationValidation,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  if (!showFedExDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        iThink Logistics Domestic Information
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="iThink Logistics Domestic Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={
              iThinkLogisticsDomesticSettings?.accountNo?.length
                ? iThinkLogisticsDomesticSettings?.accountNo
                : accountOptions
            }
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable || isLoading}
            id="pickup_address_id"
            label="Warehouse Address"
            required
            placeholder="Select"
            name="pickup_address_id"
            value={values.pickup_address_id ? [values.pickup_address_id] : []}
            options={combinedPickupAddressOptions}
            onChange={handleWarehouseChange}
            error={errors.pickup_address_id as string}
          />
          {isLoading && (
            <Typography variant="caption" sx={{ color: 'primary.main', mt: 1 }}>
              Adding warehouse, please wait...
            </Typography>
          )}
        </Grid>

        {/* Rest of your existing JSX remains the same */}
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="logistics"
            label="Logistics"
            required
            placeholder="Select"
            name="logistics"
            value={values.logistics ? [values.logistics] : []}
            options={LogisticsOptions}
            onChange={val => {
              const selected = val[0] || '';
              setFieldValue('logistics', selected);
            }}
            error={errors.logistics as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="order_type"
            label="Order Type"
            required
            placeholder="Select"
            name="order_type"
            value={values.order_type ? [values.order_type] : []}
            options={OrderTypeOptions}
            onChange={val => setFieldValue('order_type', val[0] || '')}
            error={errors.order_type as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="payment_mode"
            label="Payment Mode"
            required
            placeholder="Select"
            name="payment_mode"
            value={values.payment_mode ? [values.payment_mode] : []}
            options={PaymentModeOptions}
            onChange={val => setFieldValue('payment_mode', val[0] || '')}
            error={errors.payment_mode as string}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="s_type"
            label="Service Type"
            required
            placeholder="Select"
            name="s_type"
            value={values.s_type ? [values.s_type] : []}
            options={ServiceTypeOptions}
            onChange={val => {
              const selected = val[0] || '';
              setFieldValue('s_type', selected);
            }}
            error={errors.s_type as string}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable || isLoading}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {iThinkLogisticsDomesticDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default IThinkLogisticsDomesticInformation;
